import { computed, readonly, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '~/stores/auth'
import { useSettingsStore } from '~/stores/settings'

const STORAGE_KEY_PREFIX = 'userContext_'

// Create a global reactive event ID that all components can watch
const globalEventId = ref<string | null>(null)

export const useContext = () => {
  const authStore = useAuthStore()
  const { user } = storeToRefs(authStore)
  const settingsStore = useSettingsStore()

  const getStorageKey = (): string | null => {
    const userId = user.value?.id
    return userId ? `${STORAGE_KEY_PREFIX}${userId}` : null
  }

  const getEventId = (): string | null => {
    if (!process.client) return null
    const storageKey = getStorageKey()
    if (!storageKey) return null
    
    return localStorage.getItem(storageKey);
  }

  const setEventId = (eventId: string): void => {
    const storageKey = getStorageKey()
    if (!storageKey || !eventId) return
    
    try {
      localStorage.setItem(storageKey, eventId)
      globalEventId.value = eventId
    } catch (error) {
      console.error('Failed to save context:', error)
    }
  }

  const clearEventId = (): void => {
    const storageKey = getStorageKey()
    if (!storageKey) return
    
    try {
      localStorage.removeItem(storageKey)
      globalEventId.value = null
      // Also clear the settings store
      settingsStore.setGlobalEvent('')
    } catch (error) {
      console.error('Failed to clear context:', error)
    }
  }

  const getCurrentEventId = (): string | null => {
    return globalEventId.value || getEventId()
  }

  const hasValidContext = (): boolean => {
    return getEventId() !== null
  }

  // Initialize the global eventId if not set
  if (process.client && !globalEventId.value) {
    globalEventId.value = getEventId()
  }

  return {
    getEventId,
    setEventId,
    clearEventId,
    getCurrentEventId,
    hasValidContext,
    eventId: computed(() => globalEventId.value || getEventId()),
    reactiveEventId: readonly(globalEventId),
    currentEventId: computed(() => globalEventId.value || getEventId()),
  }
}
